/**
 * Participant-data access layer.
 *
 * `useParticipants(filter?)` reads the space's participants — the display
 * profile of everyone the space recorded, optionally narrowed by their
 * attributes or to the authors of visible comments (`hasCommented`) — via the
 * `participants.list` oRPC endpoint, returning a stable empty array while
 * loading so consumers never branch on loading. Each entry is a resolved
 * `UserProfile`, so rendering a participant needs no further fetch.
 * `invalidateParticipants` refetches every variant after a local comment
 * write.
 */
import type { Participant, ParticipantFilter } from '@repo/shared/orpc/procedures/participants';
import { useApiQueryUtils } from 'system/api-client-context';
import type { Datastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { useInstanceQuery } from './use-silo-query';

export type { Participant, ParticipantFilter } from '@repo/shared/orpc/procedures/participants';

const EMPTY_PARTICIPANTS: Participant[] = [];

/**
 * @public
 */
export function useParticipants(filter?: ParticipantFilter): Participant[] {
    const utils = useApiQueryUtils();
    const { data } = useInstanceQuery({ procedure: utils.participants.list, input: { query: filter } });
    return data?.participants ?? EMPTY_PARTICIPANTS;
}

/**
 * @public
 */
export function invalidateParticipants(datastore: Datastore) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.participants.list.key(), refetchType: 'all' }));
}
